'use client';

import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { BrainCircuit, Loader2 } from "lucide-react";
import { useTranslations } from 'next-intl';
import { cn } from "@/lib/utils";
import { useRtl } from '@/utils/hooks/useRTL';

interface StarkySenseButtonProps {
  isLoading: boolean;
  hasProblems: boolean;
  onShowDialog: () => void;
  className?: string;
  minimized?: boolean;
}

export function StarkySenseButton({
  isLoading,
  hasProblems,
  onShowDialog,
  className = "",
  minimized = false
}: StarkySenseButtonProps) {
  const t = useTranslations('starkySenseButton');
  const { direction } = useRtl();

  if (isLoading) {
    return (
      <Button
        variant="secondary"
        disabled
        dir={direction}
        className={cn(
          "transition-all duration-300 ease-in-out",
          "bg-gradient-to-r hover:from-fuchsia-400 hover:to-blue-300 from-blue-400 to-fuchsia-500 text-white",
          minimized ? "h-9 w-9 p-0" : "h-9 px-3 py-0",
          className
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin transition-all duration-300 ease-in-out" />
        <span className={cn(
          "overflow-hidden text-start",
          minimized
            ? "absolute opacity-0 pointer-events-none"
            : "opacity-100"
        )}>
          {t('analyzing')}
        </span>
      </Button>
    );
  }

  if (!hasProblems) {
    return null;
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Button
          variant="secondary"
          onClick={onShowDialog}
          dir={direction}
          className={cn(
            "transition-all duration-300 ease-in-out",
            "bg-gradient-to-r hover:from-fuchsia-400 hover:to-blue-300 from-blue-400 to-fuchsia-500 text-white",
            minimized ? "h-9 w-9 p-0" : "h-9 px-3 py-0",
            className
          )}
        >
          <BrainCircuit className="h-4 w-4 transition-all duration-300 ease-in-out" />
          <span className={cn(
            "overflow-hidden text-start",
            minimized
              ? "absolute opacity-0 pointer-events-none"
              : "opacity-100"
          )}>
            {t('contextRequired')}
          </span>
        </Button>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" dir={direction}>
        <div className="flex items-start gap-4">
          <BrainCircuit className="h-16 w-16 text-fuchsia-500" />
          <div className="space-y-1 text-start">
            <h4 className="text-sm font-semibold text-start">{t('hoverTitle')}</h4>
            <p className="text-sm text-muted-foreground text-start">
              {t('hoverDescription')}
            </p>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
